// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.PermissionEmailing.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react';
import connect from 'share/enhancers/Izenda.Connect';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import Input from 'share/components/controls/Izenda.Input';
import actions from '../Izenda.PermissionActions';
import { SECTION } from 'logic/data/Izenda.PermissionConstants';
import LicenseCheckerConstants from 'logic/data/Izenda.LicenseCheckerConstants';
import CheckboxAllPermission from './Izenda.CheckboxAllPermission';
import PropTypes from 'prop-types';
import { isNil, get } from 'lodash/fp';

@connect(
  { toClass: true },
  actions
)
export default class PermissionEmailing extends Component {
  static propTypes = {
    permission: PropTypes.object.isRequired
  };

  constructor(props: any) {
    super(props);
    this.emailing = props.permission.emailing;
  }

  onChangeEmailingTenantAccess = (e: any) => {
    const { permission } = this.props;
    permission.updateTenantAccessParent(this.emailing, e.target.checked);
  };

  onChangeDeliveryMethod = (method: string) => (e: any) => {
    const { permission } = this.props;
    permission.updateData(this.emailing.deliveryMethod, method, e.target.checked);
  };

  onChangeLinkDeliveryMethod = this.onChangeDeliveryMethod('link');
  onChangeEmbeddedHTMLDeliveryMethod = this.onChangeDeliveryMethod('embeddedHTML');
  onChangeAttachmentDeliveryMethod = this.onChangeDeliveryMethod('attachment');

  onChangeDeliveryMethodTenantAccess = e => {
    const { permission } = this.props;
    permission.updateTenantAccess(this.emailing, this.emailing.deliveryMethod, e.target.checked);
  };

  onChangeAttachmentType = (type: string) => (e: any) => {
    const { permission } = this.props;
    permission.updateData(this.emailing.attachmentType, type, e.target.checked);
  };

  onChangeWordAttachmentType = this.onChangeAttachmentType('word');
  onChangeExcelAttachmentType = this.onChangeAttachmentType('excel');
  onChangePdfAttachmentType = this.onChangeAttachmentType('pdf');
  onChangeCsvAttachmentType = this.onChangeAttachmentType('csv');
  onChangeXmlAttachmentType = this.onChangeAttachmentType('xml');
  onChangeJsonAttachmentType = this.onChangeAttachmentType('json');
  onChangeDefinitionAttachmentType = this.onChangeAttachmentType('definition');

  onChangeAttachmentTypeTenantAccess = (e: any) => {
    const { permission } = this.props;
    permission.updateTenantAccess(this.emailing, this.emailing.attachmentType, e.target.checked);
  };

  renderDeliveryMethod = () => {
    const props = this.props;
    const {
      permissionStore: {
        tenantDetail
      },
      permission,
      localization,
      authorization,
      readOnly,
      isTemplatePermissions
    } = props;
    const hasExportLicense = authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.Exporting);
    const { emailing: currentEmailing } = this;
    const emailing = get('emailing', tenantDetail);

    return (
      (isNil(tenantDetail) || props.checkShowGroupCommon(emailing.deliveryMethod)) && (
        <GridLayout.Row className='u_paddingTop u_paddingBottom'>
          <GridLayout.Col cols={2} className='izenda-PermissionCategory-SubCategory'>
            {localization.getText('COMMON_LABEL_DELIVERY_METHOD')}
          </GridLayout.Col>
          <GridLayout.Col cols={9}>
            {(isNil(tenantDetail) || emailing.deliveryMethod.link) && (
              <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(currentEmailing.deliveryMethod.link)}
                    onChange={this.onChangeLinkDeliveryMethod}
                  />
                  <span>{localization.getText('COMMON_LABEL_LINK')}</span>
                </label>
              </div>
            )}
            {(isNil(tenantDetail) || emailing.deliveryMethod.embeddedHTML) && (
              <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(currentEmailing.deliveryMethod.embeddedHTML)}
                    onChange={this.onChangeEmbeddedHTMLDeliveryMethod}
                  />
                  <span>{localization.getText('COMMON_LABEL_EMBEDDED_HTML')}</span>
                </label>
              </div>
            )}
            {hasExportLicense && (isNil(tenantDetail) || emailing.deliveryMethod.attachment) && (
              <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(currentEmailing.deliveryMethod.attachment)}
                    onChange={this.onChangeAttachmentDeliveryMethod}
                  />
                  <span>{localization.getText('COMMON_LABEL_ATTACHMENT')}</span>
                </label>
              </div>
            )}
          </GridLayout.Col>
          {permission.isTenantSetup && (
            <GridLayout.Col cols={1} className='u_textCenter'>
              <Input
                type='checkbox'
                readOnly={readOnly}
                disabled={isTemplatePermissions}
                checked={props.getCheckedCommon(currentEmailing.deliveryMethod.tenantAccess)}
                onChange={this.onChangeDeliveryMethodTenantAccess}
              />
            </GridLayout.Col>
          )}
        </GridLayout.Row>
      )
    );
  };

  renderAttachmentType = () => {
    const props = this.props;
    const {
      permissionStore: {
        tenantDetail
      },
      permission,
      localization,
      authorization,
      readOnly,
      isTemplatePermissions
    } = props;
    const hasExportLicense = authorization.checkLicense(LicenseCheckerConstants.LICENSE_KEY.Exporting);
    const { emailing: currentEmailing } = this;
    const emailing = get('emailing', tenantDetail);

    return (
      (isNil(tenantDetail) || props.checkShowGroupCommon(emailing.attachmentType)) && (
        <GridLayout.Row className='u_paddingTop u_paddingBottom'>
          <GridLayout.Col cols={2} className='izenda-PermissionCategory-SubCategory'>
            {localization.getText('COMMON_LABEL_ATTACHMENT_TYPE')}
          </GridLayout.Col>
          <GridLayout.Col cols={9}>
            {hasExportLicense && (isNil(tenantDetail) || emailing.attachmentType.word) && (
              <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(currentEmailing.attachmentType.word)}
                    onChange={this.onChangeWordAttachmentType}
                  />
                  <span>Word</span>
                </label>
              </div>
            )}
            {hasExportLicense && (isNil(tenantDetail) || emailing.attachmentType.excel) && (
              <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(currentEmailing.attachmentType.excel)}
                    onChange={this.onChangeExcelAttachmentType}
                  />
                  <span>Excel</span>
                </label>
              </div>
            )}
            {hasExportLicense && (isNil(tenantDetail) || emailing.attachmentType.pdf) && (
              <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(currentEmailing.attachmentType.pdf)}
                    onChange={this.onChangePdfAttachmentType}
                  />
                  <span>PDF</span>
                </label>
              </div>
            )}
            {hasExportLicense && (isNil(tenantDetail) || emailing.attachmentType.csv) && (
              <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(currentEmailing.attachmentType.csv)}
                    onChange={this.onChangeCsvAttachmentType}
                  />
                  <span>CSV</span>
                </label>
              </div>
            )}
            {hasExportLicense && (isNil(tenantDetail) || emailing.attachmentType.xml) && (
              <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(currentEmailing.attachmentType.xml)}
                    onChange={this.onChangeXmlAttachmentType}
                  />
                  <span>XML</span>
                </label>
              </div>
            )}
            {hasExportLicense && (isNil(tenantDetail) || emailing.attachmentType.json) && (
              <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(currentEmailing.attachmentType.json)}
                    onChange={this.onChangeJsonAttachmentType}
                  />
                  <span>JSON</span>
                </label>
              </div>
            )}
            {hasExportLicense && (isNil(tenantDetail) || emailing.attachmentType.definition) && (
              <div className='u_inlineBlock izenda-PermissionCategory-group_left'>
                <label>
                  <Input
                    type='checkbox'
                    readOnly={readOnly}
                    disabled={isTemplatePermissions}
                    checked={props.getCheckedCommon(currentEmailing.attachmentType.definition)}
                    onChange={this.onChangeDefinitionAttachmentType}
                  />
                  <span>{localization.getText('PERMISSION_LABEL_DEFINITION')}</span>
                  <span className='icon-info-circled' title={localization.getText('TENANT_TOOLTIP_DEFINITION_TYPE')} />
                </label>
              </div>
            )}
          </GridLayout.Col>
          {permission.isTenantSetup && (
            <GridLayout.Col cols={1} className='u_textCenter'>
              <Input
                type='checkbox'
                readOnly={readOnly}
                disabled={isTemplatePermissions}
                checked={props.getCheckedCommon(currentEmailing.attachmentType.tenantAccess)}
                onChange={this.onChangeAttachmentTypeTenantAccess}
              />
            </GridLayout.Col>
          )}
        </GridLayout.Row>
      )
    );
  };

  renderEmailSession = () => {
    const props = this.props;
    const {
      permissionStore: { tenantDetail },
      permission,
      localization,
      readOnly,
      isTemplatePermissions
    } = props;
    const emailing = get('emailing', tenantDetail);

    return (
      <GridLayout className='izenda-GridLayout-Custom'>
        <GridLayout.Row className='u_textBold u_paddingTop u_paddingBottom izenda-PermissionCategory-Header'>
          <GridLayout.Col cols={2}>
            <a
              className={this.props.getIconClass('isShowEmailing')}
              onClick={this.props.toggle.bind(this, 'isShowEmailing')}
            />
            {localization.getText('COMMON_LABEL_EMAILING')}
          </GridLayout.Col>
          <GridLayout.Col cols={9}>
            <CheckboxAllPermission
              permission={permission}
              readOnly={readOnly}
              disabled={isTemplatePermissions}
              section={this.emailing}
              tenantPermission={emailing}
              tenantDetail={tenantDetail}
            />
          </GridLayout.Col>
          {permission.isTenantSetup && (
            <GridLayout.Col cols={1} className='u_textCenter'>
              <Input
                type='checkbox'
                readOnly={readOnly}
                disabled={isTemplatePermissions}
                indeterminate={props.getIndeterminateCommon(this.emailing)}
                checked={props.getCheckedCommon(this.emailing.tenantAccess)}
                onChange={this.onChangeEmailingTenantAccess}
              />
            </GridLayout.Col>
          )}
        </GridLayout.Row>
      </GridLayout>
    );
  };

  render() {
    const props = this.props;
    const {
      permissionStore,
      permissionStore: { tenantDetail },
      permission: { fullReportAndDashboardAccess }
    } = props;

    return (
      !fullReportAndDashboardAccess &&
      (isNil(tenantDetail) || props.checkShowGroupCommon(tenantDetail.emailing)) && (
        <div className={SECTION.Emailing}>
          {this.renderEmailSession()}
          {permissionStore.isShowEmailing && (
            <GridLayout className='izenda-GridLayout-Custom'>
              {this.renderDeliveryMethod()}
              {this.renderAttachmentType()}
            </GridLayout>
          )}
        </div>
      )
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/Settings/PermissionTab/PermissionBody/Izenda.PermissionEmailing.jsx